import { useContext, useState } from "react";
import {
  writeDataToFirestore,
  USERS_ROOT,
  firestore,
  writeGroupDataToFirestore,
  GROUPS_ROOT,
  inviteUserToGroup,
} from "../../services/firebase"; // Adjust path if needed
import { doc, updateDoc, arrayUnion } from "firebase/firestore";
import { GlobalContext } from "../../contexts/GlobalContext";
import { snackBarConfigType } from "../../classes/SnackBarConfig";
import CustomSnackbar from "./CustomSnackbar";
import CloseIcon from "../../assets/icons/cross-solid";

interface CreateGroupProps {
  userId: string; // Logged-in user's ID
}

const CreateGroup: React.FC<CreateGroupProps> = ({ userId }) => {
  const { currentUser, getCurrentUser } = useContext(GlobalContext);

  const [groupName, setGroupName] = useState("");
  const [inviteCode, setInviteCode] = useState(""); // Input for unique code
  const [groupId, setGroupId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  let [snackBarConfig, setSnackBarConfig] = useState<snackBarConfigType>({
    open: false,
    severity: "success",
    message: "",
  });

  const [createdGroupName, setCreatedGroupName] = useState("");

  const handleCreateGroup = async () => {
    if (!groupName.trim()) {
      setError("Group name is required!");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const groupData = {
        name: groupName,
        createdBy: userId,
        members: [userId],
        invitesSent: [],
        createdAt: Date.now(),
      };

      const groupRef = await writeGroupDataToFirestore(GROUPS_ROOT, groupData);

      if (groupRef?.id) {
        const newGroupId = groupRef.id;
        setGroupId(newGroupId);
        setCreatedGroupName(groupName);

        await updateDoc(doc(firestore, USERS_ROOT, userId), {
          groups: arrayUnion(newGroupId),
        });

        getCurrentUser();
        setGroupName("");

        setSnackBarConfig({
          open: true,
          severity: "success",
          message: `Group successfully created!`,
        });
      }
    } catch (error) {
      console.error("Error creating group:", error);
      setError("Failed to create group. Try again.");
    }

    setLoading(false);
  };

  const handleInviteUser = async () => {
    if (!inviteCode.trim()) {
      setError("Enter a valid unique code to invite a user.");
      return;
    }
    if (!groupId) {
      setError("Create a group first before inviting users.");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await inviteUserToGroup(
        groupId,
        userId,
        inviteCode,
        createdGroupName,
        currentUser.userName
      );
      setInviteCode("");

      setSnackBarConfig({
        open: true,
        severity: "success",
        message: "User invited successfully!",
      });
    } catch (error) {
      console.error("Error inviting user:", error);
      setError(error + "| Failed to send invite.");
    }

    setLoading(false);
  };

  const handleCreateNewGroup = () => {
    setGroupId(null);
    setCreatedGroupName("");
    setGroupName("");
  };

  return (
    <>
      <CustomSnackbar snackbarConfig={snackBarConfig} />

      <div className="w-full flex flex-col gap-4 px-6 bg-white rounded-[2.4em] py-6">
        {!groupId ? (
          <>
            <h2 className="font-grgrifter tracking-wider text-2xl text-[#AFAEB3]">
              Create a Group
            </h2>

            {/* Group Name Input */}
            <div>
              <input
                type="text"
                placeholder="Enter group name"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border focus:outline-none focus:ring focus:ring-[#3A9437] rounded-xl bg-[#efedf4] disabled:opacity-50"
              />
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            </div>

            {/* Create Group Button */}
            <button
              onClick={handleCreateGroup}
              disabled={loading}
              className={`mt-3 w-full bg-[#3A9437] text-white py-2 rounded-2xl ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#3A9437]"
              }`}
            >
              {loading ? "Creating..." : "Create Group"}
            </button>
          </>
        ) : (
          <>
            {/* Display Created Group */}
            <div className="flex items-center justify-between">
              <h2 className="font-grgrifter tracking-wider text-2xl text-[#3A9437]">
                Group: {createdGroupName}
              </h2>
              <button
                onClick={handleCreateNewGroup}
                className="p-2 rounded-[0.9em] bg-[#CEE4CD]"
              >
                <CloseIcon color="#3A9437" size={20} />
              </button>
            </div>

            {/* Invite User Section */}
            <h3 className="font-grgrifter tracking-wider text-xl text-[#AFAEB3]">
              Invite a User
            </h3>
            <div>
              <input
                type="text"
                placeholder="Enter user's unique code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value)}
                disabled={loading}
                className="w-full px-3 py-2 border focus:outline-none focus:ring focus:ring-[#3A9437] rounded-xl bg-[#efedf4] disabled:opacity-50"
              />
              {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
            </div>

            <button
              onClick={handleInviteUser}
              disabled={loading}
              className={`mt-3 w-full bg-[#3A9437] text-white py-2 rounded-2xl ${
                loading ? "opacity-50 cursor-not-allowed" : "hover:bg-[#3A9437]"
              }`}
            >
              {loading ? "Inviting..." : "Send Invite"}
            </button>

            {/* Option to Create Another Group */}
          </>
        )}
      </div>
    </>
  );
};
export default CreateGroup;
